/**
 * DemandCard Component
 *
 * Displays demand for a single strain within a week.
 * Shows breakdown between recurring orders and AI predictions,
 * plus supply status indicating fulfilment level.
 *
 * @module DemandCard
 */

import { TrendingUp, Repeat, Calendar, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import SupplyStatusBadge from './SupplyStatusBadge'

/**
 * Format a date for display (e.g., "15 Nov")
 *
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
function formatDate(date) {
  if (!date) return ''
  const d = new Date(date)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/**
 * Format kg value with appropriate precision
 *
 * @param {number} kg - Weight in kg
 * @returns {string} Formatted string
 */
function formatKg(kg) {
  if (kg >= 100) {
    return `${Math.round(kg)}kg`
  }
  return `${kg.toFixed(1)}kg`
}

/**
 * Get confidence level label and colour
 *
 * @param {number} confidence - Confidence score (0-1)
 * @returns {{ label: string, className: string }}
 */
function getConfidenceLevel(confidence) {
  if (confidence >= 0.8) {
    return { label: 'High', className: 'text-green-600 bg-green-50' }
  }
  if (confidence >= 0.6) {
    return { label: 'Medium', className: 'text-amber-600 bg-amber-50' }
  }
  return { label: 'Low', className: 'text-red-600 bg-red-50' }
}

/**
 * Get border colour based on supply status
 *
 * @param {string} status - Supply status
 * @returns {string} Border class
 */
function getBorderClass(status) {
  switch (status) {
    case 'fulfilled':
      return 'border-green-300'
    case 'partial':
      return 'border-amber-300'
    case 'shortfall':
      return 'border-red-300'
    default:
      return 'border-gray-200'
  }
}

/**
 * DemandCard - Individual strain demand card with supply status
 *
 * @param {Object} props
 * @param {string} props.strainName - Name of the strain
 * @param {number} props.recurringKg - Recurring order demand in kg
 * @param {number} props.predictedKg - AI-predicted demand in kg
 * @param {number} props.predictedConfidence - Confidence score (0-1)
 * @param {number} props.totalKg - Total demand in kg
 * @param {Object} [props.supply] - Supply information
 * @param {number} [props.supply.expectedKg] - Expected supply in kg
 * @param {number} [props.supply.batchCount] - Number of batches
 * @param {string} [props.supply.confidence] - Confidence level
 * @param {string} [props.supply.status] - Supply status
 * @param {number} [props.supply.coveragePercent] - Coverage percentage
 */
export default function DemandCard({
  strainName,
  recurringKg,
  predictedKg,
  predictedConfidence,
  totalKg,
  supply
}) {
  const hasRecurring = recurringKg > 0
  const hasPredicted = predictedKg > 0
  const confidence = hasPredicted ? getConfidenceLevel(predictedConfidence) : null
  const borderClass = supply ? getBorderClass(supply.status) : 'border-gray-200'

  return (
    <div className={`bg-white border-2 rounded-lg p-4 hover:shadow-sm transition-shadow ${borderClass}`}>
      {/* Header: Strain name and total */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-900">{strainName}</h4>
        <div className="flex items-center gap-2">
          {supply && (
            <SupplyStatusBadge
              status={supply.status}
              coveragePercent={supply.coveragePercent}
              expectedKg={supply.expectedKg}
              batchCount={supply.batchCount}
              confidence={supply.confidence}
              compact
            />
          )}
          <span className="text-lg font-semibold text-gray-900">
            {formatKg(totalKg)}
          </span>
        </div>
      </div>

      {/* Breakdown */}
      <div className="space-y-2">
        {/* Recurring orders */}
        {hasRecurring && (
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <Repeat className="w-4 h-4 text-blue-500" />
              <span>Recurring</span>
            </div>
            <span className="font-medium text-gray-700">{formatKg(recurringKg)}</span>
          </div>
        )}

        {/* Predicted demand */}
        {hasPredicted && (
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <TrendingUp className="w-4 h-4 text-purple-500" />
              <span>Predicted</span>
              {confidence && (
                <span className={`text-xs px-1.5 py-0.5 rounded ${confidence.className}`}>
                  {confidence.label}
                </span>
              )}
            </div>
            <span className="font-medium text-gray-700">{formatKg(predictedKg)}</span>
          </div>
        )}
      </div>

      {/* Visual breakdown bar */}
      {hasRecurring && hasPredicted && (
        <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden flex">
          <div
            className="bg-blue-500 h-full"
            style={{ width: `${(recurringKg / totalKg) * 100}%` }}
            title={`Recurring: ${formatKg(recurringKg)}`}
          />
          <div
            className="bg-purple-400 h-full"
            style={{ width: `${(predictedKg / totalKg) * 100}%` }}
            title={`Predicted: ${formatKg(predictedKg)}`}
          />
        </div>
      )}

      {/* Gap action suggestion for shortfall/partial */}
      {supply && (supply.status === 'shortfall' || supply.status === 'partial') && supply.startBatchBy && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs">
              <Calendar className="w-3.5 h-3.5 text-amber-600" />
              <span className={supply.status === 'shortfall' ? 'text-red-600' : 'text-amber-600'}>
                Start batch by <span className="font-medium">{formatDate(supply.startBatchBy)}</span>
              </span>
            </div>
            <Link
              to="/production/batches/new"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              Create batch
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {supply.batchCount === 0 && (
            <p className="text-xs text-gray-500 mt-1">
              No production scheduled
            </p>
          )}
        </div>
      )}
    </div>
  )
}
